import { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { AudioNote } from '@highlight/common'
import { GlobalEvents } from '@highlight/ipc'
import { shareConversation, deleteAudioNoteShareLink } from '@renderer/utils/conversations/conversations'
import OverlayIpcRenderer from '@renderer/ipc/OverlayIpc.renderer'
import AudioNoteIpcRenderer from '@renderer/ipc/AudioNoteIpc.renderer'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'
import { useModals } from '@renderer/components/Modal'
import AuthIpcRenderer from '@renderer/ipc/AuthIpc.renderer'
import { useGlobalState, useGlobalStore } from '@highlight/ipc/react'
import { setOverlayInteractive } from '@highlight/ui'

type ShareStates = 'default' | 'sharing' | 'copied' | 'shared' | 'deleting'

export interface AudioNoteShareState {
  shareLink: string
  sharedAt: number
}

export interface AudioNoteEmailState {
  sentTo: string[]
  lastSentAt: number
}

interface UseShareAudioNoteOptions {
  audioNote: AudioNote | null
  meetingAppName?: string
  eventAction?: string
}

export interface ShareAudioNoteRequest {
  recipientEmail: string
  message?: string
}

export interface ShareAudioNoteResponse {
  success: boolean
  data: {
    type: 'direct_access' | 'pending_approval' | 'email_sent'
    message: string
  }
}

interface UseShareAudioNoteReturn {
  // Common state
  state: ShareStates
  isDisabled: boolean
  shareLink: string | null

  // Link sharing
  handleShare: (e?: React.MouseEvent) => Promise<void>
  linkTooltip: string

  // Email sharing
  handleEmail: (e: React.MouseEvent) => Promise<void>
  sendEmail: (audioNote: AudioNote, selectedEmails: string[]) => Promise<void>
  emailTooltip: string

  // New sharing API
  shareAudioNote: (audioNote: AudioNote, request: ShareAudioNoteRequest) => Promise<ShareAudioNoteResponse>
  isSharing: boolean
  sharingError: string | null

  // Utilities
  createShareLink: () => Promise<string | undefined>
  deleteShareLink: (audioNote: AudioNote) => Promise<void>
}

/**
 * Shared hook for audio note sharing functionality
 *
 * Combines the logic from AudioNoteShareWrapper and AudioNoteEmailWrapper
 * into a single reusable hook that handles both link sharing and email sending.
 */
export function useShareAudioNote({
  audioNote,
  meetingAppName,
  eventAction = 'share',
}: UseShareAudioNoteOptions): UseShareAudioNoteReturn {
  const [state, setState] = useState<ShareStates>('default')
  const [isSharing, setIsSharing] = useState(false)
  const [sharingError, setSharingError] = useState<string | null>(null)
  const [shareState, setShareState] = useGlobalState<AudioNoteShareState>(`audioNote.shareState.${audioNote?.id}`, {
    shareLink: '',
    sharedAt: 0,
  })
  const [emailState, setEmailState] = useGlobalStore<AudioNoteEmailState>(
    'conversations',
    `audioNote.emailState.${audioNote?.id}`,
    {
      sentTo: [],
      lastSentAt: 0,
    },
  )
  const [shareLink, setShareLink] = useState<string | null>(audioNote?.shareLink || shareState?.shareLink || null)
  const copiedTimeoutRef = useRef<NodeJS.Timeout>()
  const { openModal } = useModals()

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current)
      }
    }
  }, [])

  const createShareLink = useCallback(async (): Promise<string | undefined> => {
    setState('sharing')
    if (!audioNote) return

    try {
      const link = await shareConversation(audioNote)

      if (link) {
        setShareLink(link)

        // Update the audio note with the share link
        AudioNoteIpcRenderer.updateAudioNote({
          id: audioNote.id,
          updates: {
            shareLink: link,
          },
        })

        // Save share state to persistent storage
        setShareState({
          shareLink: link,
          sharedAt: Date.now(),
        })

        return link
      }
    } catch (err) {
      console.error('Error generating share link:', err)
      setState('default')
      GlobalEvents.emit('toast:error', {
        title: 'Share Link Failed',
        message: 'Failed to generate share link. Please try again.',
      })
      throw err
    }
  }, [audioNote])

  const deleteShareLink = useCallback(
    async (audioNote: AudioNote) => {
      if (!audioNote) return
      setState('deleting')
      try {
        await deleteAudioNoteShareLink(audioNote)
        await AudioNoteIpcRenderer.updateAudioNote({
          id: audioNote.id,
          updates: {
            shareLink: '',
          },
        })
        setShareLink(null)
        setState('default')
      } catch (err) {
        console.error('Error deleting share link:', err)
        setState('default')
        GlobalEvents.emit('toast:error', {
          title: 'Delete Failed',
          message: 'Failed to delete share link. Please try again.',
        })
      }
    },
    [audioNote, setShareLink, setState],
  )

  const handleShare = useCallback(
    async (e?: React.MouseEvent) => {
      if (e) {
        e.stopPropagation()
      }

      if (state === 'sharing' || state === 'copied') {
        // Early return if sharing state invalid
        return
      }

      if (state === 'shared' && shareLink) {
        try {
          // Copy existing share link
          await OverlayIpcRenderer.writeTextToClipboard(shareLink)

          AmplitudeRenderer.trackEvent('Audio Note Action', {
            action: 'copy link',
            isMeeting: meetingAppName,
            shareType: 'link',
          })

          // Update state to copied
          setState('copied')

          // Clear any existing timeout
          if (copiedTimeoutRef.current) {
            clearTimeout(copiedTimeoutRef.current)
          }

          copiedTimeoutRef.current = setTimeout(() => {
            setState('shared')
          }, 2000)
        } catch (err) {
          console.error('Error copying share link to clipboard:', err)
          GlobalEvents.emit('toast:error', {
            title: 'Copy Failed',
            message: 'Failed to copy link to clipboard. Please try again.',
          })
        }
      } else {
        try {
          const link = await createShareLink()

          if (link) {
            try {
              await OverlayIpcRenderer.writeTextToClipboard(link)
              setState('copied')

              // Clear any existing timeout
              if (copiedTimeoutRef.current) {
                clearTimeout(copiedTimeoutRef.current)
              }

              copiedTimeoutRef.current = setTimeout(() => {
                setState('shared')
              }, 2500)

              AmplitudeRenderer.trackEvent('Audio Note Action', {
                action: eventAction,
                isMeeting: meetingAppName,
                shareType: 'link',
              })
            } catch (err) {
              console.error('Error copying new share link to clipboard:', err)
              setState('default')
              GlobalEvents.emit('toast:error', {
                title: 'Copy Failed',
                message: 'Share link created but failed to copy to clipboard.',
              })
            }
          }
        } catch (err) {
          console.error('Error generating or copying share link:', err)
          setState('default')
          GlobalEvents.emit('toast:error', {
            title: 'Share Failed',
            message: 'Failed to create or copy share link. Please try again.',
          })
        }
      }

      // Release the overlay after sharing completes.
      setOverlayInteractive(false)
    },
    [state, shareLink, audioNote, meetingAppName, eventAction, createShareLink],
  )

  const handleEmail = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation()
      AmplitudeRenderer.trackEvent('Audio Note Action', {
        action: 'share-email-modal',
        isMeeting: meetingAppName,
        shareType: 'email',
      })

      if (!audioNote?.id) return

      openModal('audio-note-email', { audioNoteId: audioNote.id })
    },
    [audioNote, eventAction, meetingAppName, openModal],
  )

  const sendEmail = useCallback(
    async (audioNote: AudioNote, selectedEmails: string[]) => {
      const link = await createShareLink()
      if (!selectedEmails?.length || !link) return

      try {
        setState('sharing')
        const token = await AuthIpcRenderer.getAccessToken()
        const url = getChatApiRoute('api/v1/meetings/email')
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            summary: audioNote?.summary,
            id: audioNote.id,
            attendeeEmails: selectedEmails,
            title:
              audioNote?.title ||
              audioNote?.meeting?.windowTitle ||
              (audioNote.meeting?.appName ? audioNote.meeting.appName + ' Meeting' : ''),
            meetingName:
              audioNote?.title ||
              audioNote?.meeting?.windowTitle ||
              (audioNote.meeting?.appName ? audioNote.meeting.appName + ' Meeting' : ''),
            meetingLink: link,
            meetingDetails: audioNote.startedAt
              ? `${audioNote.startedAt.toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                })}` +
                (audioNote?.meeting?.attendees?.length
                  ? ' • ' + audioNote?.meeting?.attendees?.length + ' attendees'
                  : '')
              : '',
          }),
        })

        if (response.ok) {
          const persisted = emailState
          const nextSentTo = Array.from(new Set([...(persisted?.sentTo || []), ...selectedEmails]))
          setEmailState({ sentTo: nextSentTo, lastSentAt: Date.now() })

          AmplitudeRenderer.trackEvent('Audio Note Action', {
            action: eventAction === 'share' ? 'email participants' : eventAction,
            isMeeting: meetingAppName,
            sentCount: selectedEmails.length,
            shareType: 'email',
          })

          setState('copied')
          if (copiedTimeoutRef.current) {
            clearTimeout(copiedTimeoutRef.current)
          }
          copiedTimeoutRef.current = setTimeout(() => {
            setState('shared')
          }, 2000)

          setOverlayInteractive(false)
        } else {
          console.error('Failed to share meeting')
          setState('default')
          GlobalEvents.emit('toast:error', {
            title: 'Email Failed',
            message: 'Failed to send email to participants. Please try again.',
          })
        }
      } catch (error) {
        console.error('Error sending emails:', error)
        setState('default')
        GlobalEvents.emit('toast:error', {
          title: 'Email Failed',
          message: 'Failed to send email to participants. Please check your connection and try again.',
        })
      }
    },
    [audioNote, eventAction, meetingAppName, createShareLink, setShareState, setEmailState, emailState],
  )

  const linkTooltip = useMemo(() => {
    switch (state) {
      case 'sharing':
        return 'Generating share link...'
      case 'shared':
        return 'Copy link to clipboard'
      case 'copied':
        return 'Link copied!'
      default:
        return 'Create a share link'
    }
  }, [state])

  const emailTooltip = useMemo(() => {
    switch (state) {
      case 'sharing':
        return 'Sending email...'
      case 'shared':
        return 'Summary emailed to participants'
      case 'copied':
        return 'Email sent to participants'
      default:
        return 'Email summary to participants'
    }
  }, [state])

  const shareAudioNote = useCallback(
    async (audioNote: AudioNote, request: ShareAudioNoteRequest): Promise<ShareAudioNoteResponse> => {
      setIsSharing(true)
      setSharingError(null)

      try {
        const token = await AuthIpcRenderer.getAccessToken()
        const url = getChatApiRoute(`api/v1/audio-notes/${audioNote.id}/share`)

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(request),
        })

        if (!response.ok) {
          throw new Error(`Failed to share audio note: ${response.statusText}`)
        }

        const result: ShareAudioNoteResponse = await response.json()

        AmplitudeRenderer.trackEvent('Audio Note Action', {
          action: 'share-via-email',
          recipientEmail: request.recipientEmail,
          shareType: result.data.type,
        })

        return result
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to share audio note'
        setSharingError(errorMessage)

        GlobalEvents.emit('toast:error', {
          title: 'Share Failed',
          message: errorMessage,
        })

        throw err
      } finally {
        setIsSharing(false)
      }
    },
    [],
  )

  const isDisabled = state === 'sharing' || isSharing

  return {
    state,
    isDisabled,
    shareLink,
    handleShare,
    linkTooltip,
    handleEmail,
    sendEmail,
    emailTooltip,
    shareAudioNote,
    isSharing,
    sharingError,
    createShareLink,
    deleteShareLink,
  }
}
